import React, { useState, useEffect, useRef } from 'react';
import {
    Box, Typography, Paper, Button, Alert,
    Dialog, DialogTitle, DialogContent, DialogActions,
    Snackbar, Tooltip, IconButton, LinearProgress, CircularProgress,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    IosShare as ShareIcon,
    Star as StarIcon,
    Lock as LockIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    CalendarMonth as CalendarIcon,
    Link as LinkIcon,
} from '@mui/icons-material';
import { TimePicker } from '@mui/x-date-pickers';
import { format, addMonths, subMonths, startOfMonth } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { shareOrCopy } from '../utils/share';

// ── Constants ─────────────────────────────────────────────────────────────────
const DAYS_ORDER  = ['SUNDAY','MONDAY','TUESDAY','WEDNESDAY','THURSDAY','FRIDAY','SATURDAY'];
const WEEK_LABELS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const FREE_LIMIT  = 5;

const emptyWeek     = () => Object.fromEntries(DAYS_ORDER.map(d => [d, []]));
const parseTime     = (s) => { if (!s) return null; const [h,m] = s.split(':').map(Number); const d = new Date(); d.setHours(h,m,0,0); return d; };
const formatTime    = (d) => { if (!d) return null; return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}:00`; };
const formatDisplay = (s) => { if (!s) return ''; const [h,m] = s.split(':').map(Number); return `${h%12||12}:${String(m).padStart(2,'0')} ${h>=12?'PM':'AM'}`; };
const DAY_LABEL     = { SUNDAY:'Sunday', MONDAY:'Monday', TUESDAY:'Tuesday', WEDNESDAY:'Wednesday', THURSDAY:'Thursday', FRIDAY:'Friday', SATURDAY:'Saturday' };
const toDateStr     = (d) => { const y=d.getFullYear(); const m=String(d.getMonth()+1).padStart(2,'0'); const day=String(d.getDate()).padStart(2,'0'); return `${y}-${m}-${day}`; };

// ── Component ─────────────────────────────────────────────────────────────────
const Availability = () => {
    const { user, updateUser } = useAuth();
    const isPremium = user?.tier === 'PREMIUM';

    const [weekly,      setWeekly]      = useState(emptyWeek());
    const [dated,       setDated]       = useState({});
    const [loading,     setLoading]     = useState(true);
    const [saving,      setSaving]      = useState(false);
    const [error,       setError]       = useState('');
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [copied,      setCopied]      = useState(false);
    const [duration,    setDuration]    = useState(user?.meetingDurationMinutes || 30);

    const saveDuration = async (min) => {
        const prev = duration;
        setDuration(min);
        try {
            await api.put(`/users/${user.id}/meeting-duration`, { meetingDurationMinutes: min });
            if (updateUser) updateUser({ meetingDurationMinutes: min });
        } catch {
            setDuration(prev);
            setError('Could not save meeting length. Please try again.');
        }
    };
    const [upgradeOpen, setUpgradeOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [editorMode,   setEditorMode]   = useState('date');

    const today    = new Date(); today.setHours(0,0,0,0);
    const [viewDate, setViewDate] = useState(startOfMonth(new Date()));

    const [dialogOpen,  setDialogOpen]  = useState(false);
    const [dialogStart, setDialogStart] = useState(null);
    const [dialogEnd,   setDialogEnd]   = useState(null);
    const [dialogError, setDialogError] = useState('');

    const totalSlots = DAYS_ORDER.reduce((s, d) => s + weekly[d].length, 0)
        + Object.values(dated).reduce((s, arr) => s + arr.length, 0);
    const bookingUrl = `${window.location.origin}/book/${user?.username || ''}`;

    useEffect(() => {
        api.get('/schedules/me')
            .then(res => {
                const w = emptyWeek(); const d = {};
                res.data.forEach(slot => {
                    if (slot.repeatWeekly !== false) {
                        if (w[slot.dayOfWeek]) w[slot.dayOfWeek].push(slot);
                    } else if (slot.specificDate) {
                        if (!d[slot.specificDate]) d[slot.specificDate] = [];
                        d[slot.specificDate].push(slot);
                    }
                });
                setWeekly(w); setDated(d);
            })
            .catch(() => setError('Failed to load your availability.'))
            .finally(() => setLoading(false));
    }, []);

    // ── Calendar helpers ──────────────────────────────────────────────────────
    const year           = viewDate.getFullYear();
    const month          = viewDate.getMonth();
    const daysInMonth    = new Date(year, month + 1, 0).getDate();
    const firstDayOfWeek = new Date(year, month, 1).getDay();

    const calendarCells = [
        ...Array(firstDayOfWeek).fill(null),
        ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
    ];
    const rem = calendarCells.length % 7;
    if (rem !== 0) calendarCells.push(...Array(7 - rem).fill(null));
    const numRows = Math.ceil(calendarCells.length / 7);

    const getDOW        = (day) => DAYS_ORDER[new Date(year, month, day).getDay()];
    const getDateStr    = (day) => toDateStr(new Date(year, month, day));
    const hasDatedSlots = (day) => day && (dated[getDateStr(day)]?.length > 0);
    const hasWeeklySlots= (day) => day && weekly[getDOW(day)]?.length > 0;
    const isSelectedDay = (day) => day && selectedDate && getDateStr(day) === toDateStr(selectedDate);
    const isToday       = (day) => day && new Date(year, month, day).getTime() === today.getTime();
    const isPast        = (day) => day && new Date(year, month, day) < today;

    const prevMonth = () => { const p = subMonths(viewDate, 1); if (p >= startOfMonth(today)) setViewDate(p); };
    const nextMonth = () => setViewDate(addMonths(viewDate, 1));

    const handleDayClick = (day) => {
        if (!day) return;
        const d = new Date(year, month, day);
        if (selectedDate && toDateStr(d) === toDateStr(selectedDate)) { setSelectedDate(null); }
        else { setSelectedDate(d); setEditorMode('date'); }
    };

    const selDateStr        = selectedDate ? toDateStr(selectedDate) : null;
    const selDOW            = selectedDate ? DAYS_ORDER[selectedDate.getDay()] : null;
    const dateSpecificSlots = selDateStr ? (dated[selDateStr] || []) : [];
    const recurringSlots    = selDOW ? (weekly[selDOW] || []) : [];
    const currentSlots      = editorMode === 'recurring'
        ? recurringSlots
        : (dateSpecificSlots.length > 0 ? dateSpecificSlots : recurringSlots);

    // ── Slot management ───────────────────────────────────────────────────────
    const openAddDialog = () => {
        if (!isPremium && totalSlots >= FREE_LIMIT) { setUpgradeOpen(true); return; }
        setDialogStart(parseTime('09:00:00')); setDialogEnd(parseTime('17:00:00'));
        setDialogError(''); setDialogOpen(true);
    };

    const handleAddSlot = () => {
        if (!dialogStart || !dialogEnd) { setDialogError('Both times are required.'); return; }
        if (dialogEnd <= dialogStart)   { setDialogError('End must be after start.');  return; }
        const newSlot = {
            dayOfWeek: selDOW, startTime: formatTime(dialogStart), endTime: formatTime(dialogEnd),
            available: true, repeatWeekly: editorMode === 'recurring',
            ...(editorMode === 'date' ? { specificDate: selDateStr } : {}),
        };
        if (editorMode === 'recurring') { setWeekly(prev => ({ ...prev, [selDOW]: [...prev[selDOW], newSlot] })); }
        else { setDated(prev => ({ ...prev, [selDateStr]: [...(prev[selDateStr] || []), newSlot] })); }
        setDialogOpen(false);
    };

    const handleDelete = (idx) => {
        // If in 'date' mode but showing recurring slots (no date-specific slots exist),
        // delete from weekly recurring — not from the (empty) dated map.
        const deletingRecurring = editorMode === 'recurring' || (editorMode === 'date' && dateSpecificSlots.length === 0);
        if (deletingRecurring) {
            setWeekly(prev => ({ ...prev, [selDOW]: prev[selDOW].filter((_, i) => i !== idx) }));
        } else {
            setDated(prev => ({ ...prev, [selDateStr]: (prev[selDateStr] || []).filter((_, i) => i !== idx) }));
        }
    };

    const applyToAllWeekdays = () => {
        if (!selDateStr || !selDOW) return;
        const dateSlots = (dated[selDateStr] || []).map(s => ({ dayOfWeek:s.dayOfWeek, startTime:s.startTime, endTime:s.endTime, available:true, repeatWeekly:true }));
        setWeekly(prev => ({ ...prev, [selDOW]: dateSlots }));
        setEditorMode('recurring');
    };

    const handleSave = async () => {
        setSaving(true); setError('');
        try {
            const allSlots = [
                ...DAYS_ORDER.flatMap(d => weekly[d]),
                ...Object.entries(dated).flatMap(([dateStr, arr]) => arr.map(s => ({ ...s, specificDate: dateStr, repeatWeekly: false }))),
            ];
            await api.post('/schedules/me', allSlots);
            setSaveSuccess(true);
        } catch { setError('Failed to save. Please try again.'); }
        finally { setSaving(false); }
    };

    const handleUpgrade = async () => {
        try { const r = await api.post('/subscriptions/checkout'); window.location.href = r.data.url; }
        catch (e) { setError(e.response?.data?.message || 'Could not start checkout.'); setUpgradeOpen(false); }
    };

    if (loading) return (
        <Box sx={{ display:'flex', justifyContent:'center', alignItems:'center', height:'calc(100vh - 64px)' }}>
            <CircularProgress sx={{ color:'#7C3AED' }} />
        </Box>
    );

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <Box sx={{
            height: { md: 'calc(100vh - 64px)' },
            minHeight: { xs: '100vh', md: 'unset' },
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            overflow: { md: 'hidden' },
            bgcolor: '#F8FAFC',
        }}>

            {/* ── LEFT SIDEBAR ──────────────────────────────────────────────── */}
            <Box sx={{
                width: { xs: '100%', md: 256 },
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                borderRight: { md: '1px solid' },
                borderBottom: { xs: '1px solid', md: 'none' },
                borderColor: 'divider',
                bgcolor: 'white',
                overflowY: { md: 'auto' },
            }}>
                {/* Sidebar header */}
                <Box sx={{ px: 3, py: 2.5, background: 'linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)', flexShrink: 0 }}>
                    <Typography variant="subtitle1" fontWeight={800} sx={{ color: 'white', lineHeight: 1.2 }}>
                        My Availability
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        Set your open hours
                    </Typography>
                </Box>

                {/* Sidebar body */}
                <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
                    {error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}

                    {/* Booking link */}
                    <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform:'uppercase', letterSpacing:0.6, display:'block', mb:0.75 }}>
                            Your booking link
                        </Typography>
                        <Box sx={{ display:'flex', alignItems:'center', gap:1, px:1.5, py:1, borderRadius:2, bgcolor:'#F8FAFC', border:'1px solid', borderColor:'divider' }}>
                            <LinkIcon sx={{ fontSize:14, color:'#7C3AED', flexShrink:0 }} />
                            <Typography variant="caption" sx={{ fontFamily:'monospace', color:'#374151', flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontSize:'0.7rem' }}>
                                {bookingUrl}
                            </Typography>
                            <Tooltip title={copied ? 'Copied!' : 'Share'}>
                                <IconButton size="small" onClick={async () => { const r = await shareOrCopy({ title:'Book time with me', text:'Book a time with me on Quuip', url: bookingUrl }); if (r === 'copied') setCopied(true); }} sx={{ p:0.25, color:'#7C3AED' }}>
                                    <ShareIcon sx={{ fontSize:14 }} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>

                    {/* Meeting length */}
                    <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform:'uppercase', letterSpacing:0.6, display:'block', mb:0.75 }}>
                            Meeting length
                        </Typography>
                        <Box sx={{ display:'flex', gap:1, flexWrap:'wrap' }}>
                            {[15, 30, 45, 60].map(min => (
                                <Button key={min} size="small"
                                    variant={duration === min ? 'contained' : 'outlined'}
                                    onClick={() => saveDuration(min)}
                                    sx={{ minWidth:0, px:1.5, fontWeight:700, borderRadius:2, fontSize:'0.72rem',
                                          ...(duration === min
                                              ? { background:'linear-gradient(135deg,#7C3AED,#4F46E5)' }
                                              : { borderColor:'#C4B5FD', color:'#7C3AED', '&:hover':{ bgcolor:'#EDE9FE' } }) }}>
                                    {min} min
                                </Button>
                            ))}
                        </Box>
                    </Box>

                    {/* Free tier indicator */}
                    {!isPremium && (
                        <Box>
                            <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', mb:0.75 }}>
                                <Box sx={{ display:'flex', alignItems:'center', gap:0.75 }}>
                                    <StarIcon sx={{ fontSize:13, color:'#F59E0B' }} />
                                    <Typography variant="caption" fontWeight={700} sx={{ color: totalSlots >= FREE_LIMIT ? '#D97706' : 'text.secondary' }}>
                                        {totalSlots}/{FREE_LIMIT} slots used
                                    </Typography>
                                </Box>
                                {totalSlots >= FREE_LIMIT && (
                                    <Button size="small" onClick={() => setUpgradeOpen(true)} sx={{ fontWeight:700, fontSize:'0.68rem', py:0.25, px:1, minWidth:0, color:'#7C3AED' }}>
                                        Upgrade
                                    </Button>
                                )}
                            </Box>
                            <LinearProgress variant="determinate" value={Math.min((totalSlots/FREE_LIMIT)*100,100)}
                                sx={{ height:4, borderRadius:2, bgcolor:'#E5E7EB', '& .MuiLinearProgress-bar': { background: totalSlots>=FREE_LIMIT ? 'linear-gradient(90deg,#F59E0B,#EF4444)' : 'linear-gradient(90deg,#7C3AED,#4F46E5)', borderRadius:2 } }} />
                        </Box>
                    )}

                    {/* Instructions */}
                    <Box sx={{ pt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                            Click any date to set hours for that specific day, or switch to <strong>All [day]s</strong> mode to apply recurring weekly availability.
                        </Typography>
                    </Box>

                    {/* Legend */}
                    <Box sx={{ display:'flex', flexDirection:'column', gap:1 }}>
                        <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
                            <Box sx={{ width:8, height:8, borderRadius:'50%', bgcolor:'#7C3AED', flexShrink:0 }} />
                            <Typography variant="caption" color="text.secondary">Date-specific hours</Typography>
                        </Box>
                        <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
                            <Box sx={{ width:16, height:16, borderRadius:'50%', border:'2px dashed #A78BFA', flexShrink:0 }} />
                            <Typography variant="caption" color="text.secondary">Recurring weekly</Typography>
                        </Box>
                    </Box>

                    {/* Save button — pinned to bottom on desktop */}
                    <Box sx={{ mt:'auto', pt:1 }}>
                        <Button fullWidth variant="contained" size="medium" onClick={handleSave} disabled={saving}
                            sx={{ fontWeight:700, borderRadius:2.5, background:'linear-gradient(135deg,#7C3AED 0%,#4F46E5 100%)', boxShadow:'0 4px 12px rgba(124,58,237,0.3)', '&:hover':{ boxShadow:'0 6px 18px rgba(124,58,237,0.4)' } }}>
                            {saving ? 'Saving…' : 'Save availability'}
                        </Button>
                    </Box>
                </Box>
            </Box>

            {/* ── CALENDAR ──────────────────────────────────────────────────── */}
            <Box sx={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                bgcolor: 'white',
                borderRight: { md: '1px solid' },
                borderColor: 'divider',
            }}>
                {/* Month nav */}
                <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', px:3, py:2, borderBottom:'1px solid', borderColor:'divider', flexShrink:0 }}>
                    <Box onClick={prevMonth} sx={{ width:36, height:36, borderRadius:2, display:'flex', alignItems:'center', justifyContent:'center', border:'1.5px solid', borderColor:'divider', cursor:(year===today.getFullYear()&&month<=today.getMonth())?'not-allowed':'pointer', opacity:(year===today.getFullYear()&&month<=today.getMonth())?0.3:1, transition:'all 0.15s', '&:hover':{ borderColor:'#7C3AED', bgcolor:'#EDE9FE' } }}>
                        <ChevronLeftIcon fontSize="small" />
                    </Box>
                    <Typography variant="h6" fontWeight={800} sx={{ letterSpacing:-0.5 }}>{format(viewDate,'MMMM yyyy')}</Typography>
                    <Box onClick={nextMonth} sx={{ width:36, height:36, borderRadius:2, display:'flex', alignItems:'center', justifyContent:'center', border:'1.5px solid', borderColor:'divider', cursor:'pointer', transition:'all 0.15s', '&:hover':{ borderColor:'#7C3AED', bgcolor:'#EDE9FE' } }}>
                        <ChevronRightIcon fontSize="small" />
                    </Box>
                </Box>

                {/* Weekday labels */}
                <Box sx={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', bgcolor:'#FAFAFA', borderBottom:'1px solid', borderColor:'divider', flexShrink:0 }}>
                    {WEEK_LABELS.map((label, i) => (
                        <Box key={i} sx={{ py:1.25, textAlign:'center' }}>
                            <Typography variant="caption" fontWeight={700} sx={{ color:i===0||i===6?'#9CA3AF':'text.secondary', letterSpacing:0.8, fontSize:'0.72rem', textTransform:'uppercase' }}>{label}</Typography>
                        </Box>
                    ))}
                </Box>

                {/* Calendar grid — fills remaining height */}
                <Box sx={{
                    flex: 1,
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    gridTemplateRows: { md: `repeat(${numRows}, 1fr)` },
                    overflow: 'hidden',
                    bgcolor: 'white',
                }}>
                    {calendarCells.map((day, idx) => {
                        const col       = idx % 7;
                        const isWeekend = col === 0 || col === 6;
                        const isLastCol = col === 6;
                        const isLastRow = idx >= calendarCells.length - 7;
                        const dSlots    = hasDatedSlots(day);
                        const wSlots    = hasWeeklySlots(day);
                        const sel       = isSelectedDay(day);
                        const todayDay  = isToday(day);
                        const past      = isPast(day);

                        return (
                            <Box key={idx} onClick={() => handleDayClick(day)} sx={{
                                minHeight: { xs: 80, md: 0 },
                                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start',
                                pt: { xs: 1.5, md: 1 }, gap: 0.5,
                                cursor: day ? (past ? 'default' : 'pointer') : 'default',
                                bgcolor: sel ? '#7C3AED' : past ? '#F9FAFB' : isWeekend ? '#FAFAFA' : 'white',
                                opacity: past && !sel ? 0.45 : 1,
                                borderRight: isLastCol ? 'none' : '1px solid',
                                borderBottom: isLastRow ? 'none' : '1px solid',
                                borderColor: 'divider',
                                transition: 'background-color 0.15s',
                                '&:hover': day && !sel && !past ? { bgcolor: '#EDE9FE' } : {},
                            }}>
                                {day && (
                                    <>
                                        <Box sx={{
                                            width: 32, height: 32, borderRadius: '50%',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            bgcolor: todayDay && !sel ? '#EDE9FE' : 'transparent',
                                            border: sel ? '2px solid transparent'
                                                  : todayDay ? '2px solid #7C3AED'
                                                  : wSlots ? '2px dashed #A78BFA'
                                                  : '2px solid transparent',
                                        }}>
                                            <Typography variant="body2" fontWeight={dSlots||wSlots||todayDay||sel?700:400}
                                                sx={{ fontSize:'0.85rem', color: sel?'white': past?'#D1D5DB': isWeekend?'#9CA3AF':'text.primary' }}>
                                                {day}
                                            </Typography>
                                        </Box>
                                        {(dSlots || wSlots) && (
                                            <Box sx={{ width:5, height:5, borderRadius:'50%', bgcolor: sel?'rgba(255,255,255,0.9)':'#7C3AED' }} />
                                        )}
                                        {(dSlots || wSlots) && (
                                            <Typography variant="caption" sx={{ fontSize:'0.58rem', fontWeight:700, lineHeight:1, color: sel?'rgba(255,255,255,0.7)':'#7C3AED' }}>
                                                {dSlots ? `${dated[getDateStr(day)].length}×` : `${weekly[getDOW(day)].length}×`}
                                            </Typography>
                                        )}
                                    </>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            </Box>

            {/* ── RIGHT: Day editor ─────────────────────────────────────────── */}
            <Box sx={{
                width: { xs: '100%', md: 300 },
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                overflowY: 'auto',
                bgcolor: 'white',
                borderTop: { xs: '1px solid', md: 'none' },
                borderColor: 'divider',
            }}>
                {!selectedDate ? (
                    <Box sx={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', textAlign:'center', p:4, flex:1 }}>
                        <Box sx={{ width:56, height:56, borderRadius:'50%', background:'linear-gradient(135deg,#EDE9FE,#C7D2FE)', display:'flex', alignItems:'center', justifyContent:'center', mx:'auto', mb:2 }}>
                            <CalendarIcon sx={{ fontSize:26, color:'#7C3AED' }} />
                        </Box>
                        <Typography variant="subtitle2" fontWeight={700} gutterBottom>Select a date</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ maxWidth:200, mx:'auto', fontSize:'0.8rem' }}>
                            Click any date on the calendar to set your available hours.
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ display:'flex', flexDirection:'column', flex:1 }}>
                        {/* Editor header */}
                        <Box sx={{ px:3, py:2.5, background:'linear-gradient(135deg,#7C3AED 0%,#4F46E5 100%)', flexShrink:0 }}>
                            {/* Mode tabs */}
                            <Box sx={{ display:'flex', gap:1, mb:1.5 }}>
                                {[['date','This day'],['recurring',`All ${DAY_LABEL[selDOW]}s`]].map(([mode, label]) => (
                                    <Box key={mode} onClick={() => setEditorMode(mode)} sx={{ px:2, py:0.4, borderRadius:2, cursor:'pointer', bgcolor: editorMode===mode ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.2)', transition:'all 0.15s', '&:hover':{ bgcolor:'rgba(255,255,255,0.2)' } }}>
                                        <Typography variant="caption" sx={{ color:'white', fontWeight:700, fontSize:'0.7rem' }}>{label}</Typography>
                                    </Box>
                                ))}
                            </Box>
                            <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                                <Box>
                                    <Typography variant="caption" sx={{ color:'rgba(255,255,255,0.65)', textTransform:'uppercase', letterSpacing:0.8, fontSize:'0.65rem' }}>
                                        {editorMode==='date' ? 'Date-specific' : 'Recurring every week'}
                                    </Typography>
                                    <Typography variant="subtitle1" fontWeight={700} sx={{ color:'white', lineHeight:1.2 }}>
                                        {editorMode==='date' ? format(selectedDate,'MMM d, yyyy') : DAY_LABEL[selDOW]}
                                    </Typography>
                                </Box>
                                <Button startIcon={!isPremium&&totalSlots>=FREE_LIMIT ? <LockIcon sx={{ fontSize:15 }} /> : <AddIcon sx={{ fontSize:15 }} />}
                                    onClick={openAddDialog} size="small"
                                    sx={{ bgcolor:'rgba(255,255,255,0.18)', color:'white', fontWeight:700, borderRadius:2, border:'1.5px solid rgba(255,255,255,0.3)', '&:hover':{ bgcolor:'rgba(255,255,255,0.28)' }, boxShadow:'none', fontSize:'0.78rem', px:1.5 }}>
                                    {!isPremium&&totalSlots>=FREE_LIMIT ? 'Unlock' : 'Add'}
                                </Button>
                            </Box>
                        </Box>

                        {/* Apply to all weekdays banner */}
                        {editorMode==='date' && (dated[selDateStr]?.length > 0) && (
                            <Box sx={{ px:3, py:1.25, bgcolor:'#F5F3FF', borderBottom:'1px solid #EDE9FE', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
                                <Typography variant="caption" sx={{ color:'#6D28D9', fontWeight:500, fontSize:'0.72rem' }}>
                                    Apply to every {DAY_LABEL[selDOW]}?
                                </Typography>
                                <Button size="small" onClick={applyToAllWeekdays} sx={{ color:'#7C3AED', fontWeight:700, fontSize:'0.7rem', py:'1px', px:1, minWidth:0 }}>
                                    Apply →
                                </Button>
                            </Box>
                        )}

                        {/* Slot list */}
                        <Box sx={{ px:3, py:2.5, flex:1, overflowY:'auto' }}>
                            {editorMode==='date' && dateSpecificSlots.length===0 && recurringSlots.length>0 && (
                                <Typography variant="caption" sx={{ display:'block', color:'#7C3AED', fontWeight:600, mb:1.5, opacity:0.7 }}>
                                    Recurring every {DAY_LABEL[selDOW]}
                                </Typography>
                            )}
                            {currentSlots.length === 0 ? (
                                <Box sx={{ textAlign:'center', py:3 }}>
                                    <Typography color="text.secondary" variant="body2" sx={{ fontSize:'0.82rem' }}>
                                        {editorMode==='date'
                                            ? `No hours set for ${format(selectedDate,'MMM d')} yet.`
                                            : `No recurring hours for ${DAY_LABEL[selDOW]}s yet.`}
                                    </Typography>
                                    <Typography variant="caption" color="text.disabled">Tap "Add" above to get started.</Typography>
                                </Box>
                            ) : (
                                <Box sx={{ display:'flex', flexDirection:'column', gap:1.5 }}>
                                    {currentSlots.map((slot, i) => (
                                        <Box key={i} sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', px:2, py:1.25, borderRadius:2.5, border:'1.5px solid #7C3AED', bgcolor:'#F5F3FF' }}>
                                            <Typography variant="body2" fontWeight={700} sx={{ color:'#7C3AED', fontSize:'0.85rem' }}>
                                                {formatDisplay(slot.startTime)} – {formatDisplay(slot.endTime)}
                                            </Typography>
                                            <IconButton size="small" onClick={() => handleDelete(i)} sx={{ p:0.25, color:'#7C3AED', opacity:0.55, '&:hover':{ opacity:1 } }}>
                                                <DeleteIcon sx={{ fontSize:14 }} />
                                            </IconButton>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Box>
                    </Box>
                )}
            </Box>

            {/* ── Dialogs ───────────────────────────────────────────────────── */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx:{ borderRadius:4 } }}>
                <DialogTitle sx={{ fontWeight:700, pb:1 }}>
                    {editorMode==='date' && selectedDate
                        ? `Add time — ${format(selectedDate,'MMM d, yyyy')}`
                        : `Recurring — ${selDOW ? DAY_LABEL[selDOW]+'s' : ''}`}
                </DialogTitle>
                <DialogContent sx={{ pt:1 }}>
                    {dialogError && <Alert severity="error" sx={{ mb:2 }}>{dialogError}</Alert>}
                    <Box sx={{ display:'flex', gap:2, flexDirection:'column', mt:1 }}>
                        <TimePicker label="Start time" value={dialogStart} onChange={setDialogStart} sx={{ width:'100%' }} />
                        <TimePicker label="End time"   value={dialogEnd}   onChange={setDialogEnd}   sx={{ width:'100%' }} />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px:3, pb:2.5, gap:1 }}>
                    <Button onClick={() => setDialogOpen(false)} sx={{ borderRadius:2 }}>Cancel</Button>
                    <Button variant="contained" onClick={handleAddSlot} sx={{ background:'linear-gradient(135deg,#7C3AED 0%,#4F46E5 100%)', fontWeight:700, borderRadius:2 }}>Add</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={upgradeOpen} onClose={() => setUpgradeOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx:{ borderRadius:4 } }}>
                <DialogTitle sx={{ display:'flex', alignItems:'center', gap:1, fontWeight:700, pb:1 }}>
                    <StarIcon sx={{ color:'#F59E0B' }} /> You've hit the free limit
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1" gutterBottom>The free plan includes <strong>{FREE_LIMIT} time slots</strong>.</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt:1 }}>Upgrade to Premium for unlimited slots.</Typography>
                </DialogContent>
                <DialogActions sx={{ px:3, pb:2.5, gap:1 }}>
                    <Button onClick={() => setUpgradeOpen(false)}>Maybe later</Button>
                    <Button variant="contained" onClick={handleUpgrade} sx={{ background:'linear-gradient(135deg,#7C3AED 0%,#4F46E5 100%)', fontWeight:700, borderRadius:2 }}>Upgrade to Premium</Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={saveSuccess} autoHideDuration={3000} onClose={() => setSaveSuccess(false)} message="Availability saved!" />
            <Snackbar open={copied}      autoHideDuration={2000} onClose={() => setCopied(false)}      message="Link copied!" />
        </Box>
    );
};

export default Availability;
